"use client";

import React, { useState } from 'react';
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    Input,
    VStack,
    Heading,
    useToast,
    Container,
    Center,
    Spinner,
    Text,
    InputGroup,
    InputRightElement,
    IconButton,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';

// Define props interface
interface LoginPageProps {
    onLoginSuccess: () => void; // Function to call on successful login
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const toast = useToast();

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!email || !password) {
            toast({
                title: 'Credentials required.',
                description: 'Please enter both email and password.',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await authService.login(email, password);

            toast({
                title: 'Login Successful.',
                description: 'Welcome back!',
                status: 'success',
                duration: 2000,
                isClosable: true,
            });
            onLoginSuccess(); // Call the callback to update App state
            // No need to navigate here, App component will re-render and show DashboardLayout
            // navigate('/');
        } catch (err: any) {
            console.error("Login failed:", err);
            const errorMessage = err.message || 'An unknown error occurred during login.';
            setError(errorMessage);
            toast({
                title: 'Login Failed.',
                description: errorMessage,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Center h="100vh" bg="gray.50">
            <Container maxW="md">
                <Box p={8} borderWidth={1} borderRadius="lg" boxShadow="lg" bg="white">
                    <VStack spacing={4} as="form" onSubmit={handleSubmit}>
                        <Heading as="h1" size="lg" textAlign="center" mb={4}>
                            Dashboard Login
                        </Heading>
                        <FormControl id="email" isRequired>
                            <FormLabel>Email</FormLabel>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email"
                                isDisabled={isLoading}
                            />
                        </FormControl>
                        <FormControl id="password" isRequired>
                            <FormLabel>Password</FormLabel>
                            <InputGroup>
                                <Input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    isDisabled={isLoading}
                                />
                                <InputRightElement>
                                    <IconButton
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                        icon={showPassword ? <ViewIcon /> : <ViewOffIcon />}
                                        onClick={() => setShowPassword(!showPassword)}
                                        variant="ghost"
                                        size="md"
                                        isDisabled={isLoading}
                                    />
                                </InputRightElement>
                            </InputGroup>
                        </FormControl>
                        {error && (
                            <Text color="red.500" fontSize="sm">
                                {error}
                            </Text>
                        )}
                        <Button
                            type="submit"
                            colorScheme="blue"
                            width="full"
                            isLoading={isLoading}
                            spinner={<Spinner size="sm" />}
                            mt={4}
                        >
                            {isLoading ? 'Logging in...' : 'Login'}
                        </Button>
                    </VStack>
                </Box>
            </Container>
        </Center>
    );
};

export default LoginPage; 